import { useUser } from "@libs:react-query-auth";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  SxProps,
  Tooltip,
  Typography,
} from "@mui/material";
import { getFirstCharacter } from "@utils:string";
import { useState } from "react";
import { BiLogOut } from "react-icons/bi";
import { FaGear } from "react-icons/fa6";
import { useNavigate } from "react-router";

import { Routes } from "@:constants";

interface AccountMenuProps {
  sx?: SxProps;
}

export const AccountMenu = ({ sx }: AccountMenuProps) => {
  const navigate = useNavigate();

  const currentUser = useUser().data;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box className="AccountInfo">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          textAlign: "center",
          ...sx,
        }}
      >
        <Tooltip title="Account settings">
          <IconButton onClick={handleClick}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                backgroundColor: "#EEEDF3",
                color: "#161447",
                fontWeight: "600",
              }}
            >
              {getFirstCharacter(currentUser!.email)}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              width: "240px",
              borderRadius: ".5rem",
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
      >
        <MenuItem
          dense
          sx={{
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              sx={{
                display: "block",
                fontSize: "0.75rem",
                color: "gray.500",
                lineHeight: "1rem",
              }}
            >
              Logged in as
            </Typography>
            <Typography
              sx={{
                marginTop: "0.125rem",
                fontWeight: "600",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {currentUser!.email}
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            navigate(Routes.SettingsProfile, {
              replace: true,
            });
            handleClose();
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <FaGear />
          Settings
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() =>
            navigate(Routes.Logout, {
              replace: true,
            })
          }
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <BiLogOut />
          Log out
        </MenuItem>
      </Menu>
    </Box>
  );
};
